import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { Button } from './ui/button';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { isIOS } from '../utils/install-platform';

const EXCLUDED_PATHS = ['/', '/login', '/register'];
const STORAGE_KEY = 'pwa-install-banner-dismissed-at';
const HIDE_FOR_DAYS = 7;

function getDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseInt(raw, 10) : null;
  } catch {
    return null;
  }
}

function isDismissedWithinPeriod(): boolean {
  const dismissedAt = getDismissedAt();
  if (dismissedAt == null) return false;
  const now = Date.now();
  return now - dismissedAt < HIDE_FOR_DAYS * 24 * 60 * 60 * 1000;
}

function setDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export const InstallBanner: React.FC = () => {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const { canInstall, isInstalled, promptInstall } = useInstallPrompt();
  const [userDismissed, setUserDismissed] = React.useState(isDismissedWithinPeriod());

  React.useEffect(() => {
    setUserDismissed(isDismissedWithinPeriod());
  }, []);

  const handleInstall = React.useCallback(async (): Promise<void> => {
    await promptInstall();
    setDismissed();
  }, [promptInstall]);

  const handleLater = React.useCallback((): void => {
    setDismissed();
    setUserDismissed(true);
  }, []);

  const excluded = EXCLUDED_PATHS.includes(pathname);
  const showChromeBanner =
    canInstall && !isInstalled && !userDismissed && !excluded;
  const showIOSBanner =
    !canInstall && isIOS() && !isInstalled && !userDismissed && !excluded;
  const showFallbackBanner =
    !canInstall && !isIOS() && !isInstalled && !userDismissed && !excluded;

  if (showChromeBanner) {
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          left: '50%',
          top: '1rem',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          maxWidth: 'calc(100vw - 2rem)',
          boxSizing: 'border-box',
          backgroundColor: 'var(--scribane-black)',
          color: '#ffffff',
          padding: '0.75rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <span style={{ fontSize: '0.875rem' }}>{t('pwa_install_banner_message')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <Button size="sm" onClick={() => void handleInstall()}>
            {t('pwa_install_app')}
          </Button>
          <Button size="sm" variant="outline" onClick={handleLater}>
            {t('pwa_install_later')}
          </Button>
        </div>
      </div>
    );
  }

  if (showIOSBanner) {
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          left: '50%',
          top: '1rem',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          maxWidth: 'calc(100vw - 2rem)',
          boxSizing: 'border-box',
          backgroundColor: 'var(--scribane-black)',
          color: '#ffffff',
          padding: '0.75rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem' }}>{t('pwa_install_banner_ios_message')}</span>
          <Button size="sm" variant="outline" onClick={handleLater}>
            {t('pwa_install_got_it')}
          </Button>
        </div>
        <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>{t('pwa_install_banner_ios_hint')}</span>
      </div>
    );
  }

  if (showFallbackBanner) {
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          left: '50%',
          top: '1rem',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          maxWidth: 'calc(100vw - 2rem)',
          boxSizing: 'border-box',
          backgroundColor: 'var(--scribane-black)',
          color: '#ffffff',
          padding: '0.75rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <span style={{ fontSize: '0.875rem' }}>{t('pwa_install_banner_fallback')}</span>
        <Button size="sm" variant="outline" onClick={handleLater} style={{ flexShrink: 0 }}>
          {t('pwa_install_got_it')}
        </Button>
      </div>
    );
  }

  return null;
};
